import {
  FontUpdate,
  getInheritedFontTypeLabel,
} from 'client/components/core/Sidebar/components/Settings/components/Font/BaseFont'
import { useDeviceModeContext } from 'client/context/DeviceModeContext'
import { usePage } from 'client/store'
import {
  isWebsitePageType,
  pageSelectors,
} from 'client/store/page/pageSelectors'
import { useCallback } from 'react'
import { getMobileOrDesktop } from 'src/shared/utils/get-mobile-or-desktop'

export type UseFontSelectorProps<
  Object extends Record<string, any>,
  FontFamilyKey extends keyof Object,
  FontWeightKey extends keyof Object,
  FontStyleKey extends keyof Object,
  FontFileIdKey extends keyof Object,
  MobileFontFamilyKey extends keyof Object | undefined = undefined,
  MobileFontWeightKey extends keyof Object | undefined = undefined,
  MobileFontStyleKey extends keyof Object | undefined = undefined,
  MobileFontFileIdKey extends keyof Object | undefined = undefined,
> = {
  object: Object
  fontFamilyKey: FontFamilyKey
  fontWeightKey: FontWeightKey
  fontStyleKey: FontStyleKey
  fontFileIdKey: FontFileIdKey
  mobileFontFamilyKey?: MobileFontFamilyKey
  mobileFontWeightKey?: MobileFontWeightKey
  mobileFontStyleKey?: MobileFontStyleKey
  mobileFontFileIdKey?: MobileFontFileIdKey
  updateObject: (object: Object) => void
}

export const useFontSelector = <
  Object extends Record<string, any>,
  FontFamilyKey extends keyof Object,
  FontWeightKey extends keyof Object,
  FontStyleKey extends keyof Object,
  FontFileIdKey extends keyof Object,
  MobileFontFamilyKey extends keyof Object | undefined = undefined,
  MobileFontWeightKey extends keyof Object | undefined = undefined,
  MobileFontStyleKey extends keyof Object | undefined = undefined,
  MobileFontFileIdKey extends keyof Object | undefined = undefined,
>({
  object,
  fontFamilyKey: fontFamilyKeyFromProps,
  fontWeightKey: fontWeightKeyFromProps,
  fontStyleKey: fontStyleKeyFromProps,
  fontFileIdKey: fontFileIdKeyFromProps,
  mobileFontFamilyKey,
  mobileFontWeightKey,
  mobileFontStyleKey,
  mobileFontFileIdKey,
  updateObject,
}: UseFontSelectorProps<
  Object,
  FontFamilyKey,
  FontWeightKey,
  FontStyleKey,
  FontFileIdKey,
  MobileFontFamilyKey,
  MobileFontWeightKey,
  MobileFontStyleKey,
  MobileFontFileIdKey
>) => {
  const { isMobile } = useDeviceModeContext()
  const pageType = usePage(pageSelectors.getPageType)
  const parentGlobalSettings = usePage(pageSelectors.getParentGlobalSettings)
  const isWebsitePage = usePage(isWebsitePageType)

  let inheritedFontTypeLabel = getInheritedFontTypeLabel(pageType)
  if (
    parentGlobalSettings &&
    Object.keys(parentGlobalSettings).length === 0 &&
    !isWebsitePage
  ) {
    inheritedFontTypeLabel = 'settings_styles.font_family.inherited'
  }

  const fontFamilyKey = getMobileOrDesktop(
    fontFamilyKeyFromProps,
    mobileFontFamilyKey,
    isMobile,
  )
  const fontWeightKey = getMobileOrDesktop(
    fontWeightKeyFromProps,
    mobileFontWeightKey,
    isMobile,
  )
  const fontStyleKey = getMobileOrDesktop(
    fontStyleKeyFromProps,
    mobileFontStyleKey,
    isMobile,
  )
  const fontFileIdKey = getMobileOrDesktop(
    fontFileIdKeyFromProps,
    mobileFontFileIdKey,
    isMobile,
  )

  const updateObjectFont = useCallback(
    (updatedFont: FontUpdate) => {
      const updatedObject = { ...object }
      const newFontProperties: Partial<Object> = {}
      if (updatedFont.fontFileId) {
        newFontProperties[fontFamilyKey] =
          updatedFont.fontFamily as Object[FontFamilyKey]
        newFontProperties[fontFileIdKey] =
          updatedFont.fontFileId as Object[FontFileIdKey]
        delete updatedObject[fontStyleKey]
        delete updatedObject[fontWeightKey]
      } else {
        newFontProperties[fontFamilyKey] =
          updatedFont.fontFamily as Object[FontFamilyKey]
        if (updatedFont.fontFamily === 'inherit') {
          updatedObject[fontStyleKey] = 'inherit' as Object[FontWeightKey]
          updatedObject[fontWeightKey] = 'inherit' as Object[FontWeightKey]
        } else {
          if (updatedFont.fontStyle) {
            newFontProperties[fontStyleKey] =
              updatedFont.fontStyle as Object[FontStyleKey]
          }
          if (updatedFont.fontWeight) {
            newFontProperties[fontWeightKey] =
              updatedFont.fontWeight as Object[FontWeightKey]
          }
        }
        if (updatedObject[fontFileIdKey]) {
          delete updatedObject[fontFileIdKey]
        }
      }
      updateObject({
        ...updatedObject,
        ...newFontProperties,
      })
    },
    [
      object,
      fontFamilyKey,
      fontFileIdKey,
      fontStyleKey,
      fontWeightKey,
      updateObject,
    ],
  )

  return {
    inheritedFontTypeLabel,
    updateObjectFont,
  }
}
